import { useParams, useNavigate } from 'react-router-dom';
import { getWindmillSingleFlowApi } from '../../utils/apis';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Button } from '@mui/material';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import WindmillFlowSidebar from '../../components/Windmill/WindmillFlowSidebar';
import FlowArea from './FlowArea';

import { useDispatch, useSelector } from 'react-redux';
import { clearWindmillJobId } from '../../utils/redux/slices/windmillFlowSlice';
import { RootState } from '../../utils/redux/redux';

import { HeaderContainer } from '../../components/dashboard/AgentInfoHeader';
import { routes } from '../../constants/routes';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { useEffect } from 'react';
import FlowDetailSkeleton from '../../components/Windmill/FlowDetailSkeleton';
import NoDataFallback from '../../components/common/NoDataFallback';

const FlowDetail = () => {
  const { flowPath } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const windmillJobId = useSelector(
    (state: RootState) => state.windmillFlow.windmillJobId
  );

  // Convert hyphens back to forward slashes for the API call
  const originalPath = flowPath ? flowPath.replace(/-/g, '/') : '';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['windmill-flow', originalPath],
    queryFn: () => getWindmillSingleFlowApi(originalPath),
    enabled: !!originalPath,
  });

  // Clean the flow name by removing the u/quibbleaiio/ prefix
  const cleanFlowName = originalPath.replace(/^u\/quibbleaiio\//, '');

  const handleBackToWorkflows = () => {
    navigate(routes.quibbleFlows);
  };

  // Clear windmillJobId when component mounts to prevent stale state
  useEffect(() => {
    dispatch(clearWindmillJobId());
  }, [dispatch]);

  // REMOVED: Auto-navigation useEffect - navigation now happens on click

  if (isLoading) return <FlowDetailSkeleton />;
  if (isError)
    return (
      <NoDataFallback
        message="Error loading flow"
        description="There is an error loading the flow."
      />
    );

  return (
    <LayoutWithSidebar margin={0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <HeaderContainer
          sx={{
            borderRadius: 0,
            border: 'none',
            borderBottom: '1px solid #DCE0E7',
            margin: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              onClick={handleBackToWorkflows}
              sx={{
                minWidth: 'auto',
                p: 0,
                color: 'text.primary',
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              <ArrowLeftIcon size={24} />
            </Button>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'DM Sans',
                  fontWeight: 600,
                  fontSize: '24px',
                  lineHeight: '100%',
                  letterSpacing: '-0.01em',
                  color: '#2B2D3F',
                  mb: 0.5,
                }}
              >
                {cleanFlowName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '14px',
                }}
              >
                {data?.data?.summary ||
                  'Orchestrates the process of transferring calls between agents'}
              </Typography>
            </Box>
          </Box>
        </HeaderContainer>

        {/* Main Content Area */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Main Flow Area */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <FlowArea flowName={originalPath} flowData={data?.data} />
          </Box>

          {/* Execution History Sidebar */}
          <Box
            sx={{
              width: 320,
              borderLeft: '1px solid #DCE0E7',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Execution History Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #DCE0E7',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: '#2B2D3F',
                  letterSpacing: '-0.2px',
                }}
              >
                Execution History
              </Typography>
            </Box>

            {/* Execution History Content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <WindmillFlowSidebar
                flowName={originalPath}
                flowPath={flowPath}
                selectedJobId={windmillJobId}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default FlowDetail;
